import { useEffect, useRef, useState } from 'react'
import type { GeometryResponse, MediaAssetPublic, Waypoint } from '@xingroute/schema'
import { ErrorBoundary } from '../App.js'
import { useLocale } from '../locale/LocaleContext.js'
import { AuthorizedMediaImage } from './AuthorizedMediaImage.js'
import { RouteMap, type MapStyle } from './RouteMap.js'

// CoverStage — hero region of the route detail page. A primary toggle
// switches between map and gallery; RouteMap is mounted continuously and
// merely hidden when the gallery is active so flips carry no setup cost.

export type Stage = 'map' | 'gallery'

export interface CoverStageProps {
  geometry: GeometryResponse | null
  media: MediaAssetPublic[]
  mediaToken?: string | null
  canEdit: boolean
  onWaypointsStateChange?: (state: { editing: boolean; waypoints: Waypoint[] }) => void
  focusWaypoint?: { id: string; nonce: number } | null
  pinnedWaypoints?: string[]
  activeWaypointId?: string | null
  onWaypointClick?: (id: string) => void
  /** Waypoint ids with at least one attachment (photo glyph on the map). */
  attachmentWaypointIds?: ReadonlySet<string>
  showWaypointsOnMap?: boolean
  stage?: Stage
  onStageChange?: (stage: Stage) => void
  styleRequest?: { style: MapStyle; nonce: number } | null
  onMapStyleChange?: (style: MapStyle) => void
  onEditRoute?: () => void
  mapEditMode?: 'inline' | 'external' | 'none'
  /** External request to flip the stage to gallery at a specific index.
   *  Carries a nonce so the same media id can be re-requested after the
   *  user pans somewhere else. */
  focusMedia?: { mediaId: string; nonce: number } | null
  /** Editor "click-to-add-waypoint" one-shot mode — see RouteMapProps. */
  addingWaypoint?: boolean
  onWaypointAddRequest?: (result: { ok: true; lat: number; lon: number; ele?: number }) => void
}

export function CoverStage({
  geometry,
  media,
  mediaToken,
  canEdit,
  onWaypointsStateChange,
  focusWaypoint,
  pinnedWaypoints,
  activeWaypointId,
  onWaypointClick,
  attachmentWaypointIds,
  showWaypointsOnMap = false,
  stage: controlledStage,
  onStageChange,
  styleRequest,
  onMapStyleChange,
  onEditRoute,
  mapEditMode = onEditRoute ? 'external' : 'inline',
  focusMedia,
  addingWaypoint = false,
  onWaypointAddRequest,
}: CoverStageProps) {
  const { t, locale } = useLocale()
  const [internalStage, setInternalStage] = useState<Stage>('map')
  const [galleryIndex, setGalleryIndex] = useState(0)
  const mapHostRef = useRef<HTMLDivElement | null>(null)
  const stage = controlledStage ?? internalStage

  function setStage(next: Stage) {
    setInternalStage(next)
    onStageChange?.(next)
  }

  useEffect(() => {
    if (galleryIndex >= media.length) {
      setGalleryIndex(Math.max(0, media.length - 1))
    }
  }, [media.length, galleryIndex])

  useEffect(() => {
    if (stage === 'map' && mapHostRef.current) {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    }
  }, [stage])

  useEffect(() => {
    if (focusWaypoint) setStage('map')
  }, [focusWaypoint])

  // External request to focus a media item — flip to gallery + jump index.
  // Re-fires on every nonce bump even when mediaId is identical so the same
  // thumbnail can be clicked twice in a row.
  useEffect(() => {
    if (!focusMedia) return
    const idx = media.findIndex((m) => m.id === focusMedia.mediaId)
    if (idx < 0) return
    setStage('gallery')
    setGalleryIndex(idx)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMedia])

  const hasMedia = media.length > 0
  const active = hasMedia ? media[Math.min(galleryIndex, media.length - 1)]! : null

  return (
    <section
      className="group flex h-full min-h-0 flex-col overflow-hidden rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)]"
      data-testid="cover-stage"
    >
      <div className="flex flex-none items-center gap-2 border-b border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2">
        <div className="flex items-center" role="tablist" aria-label={t('aria.content')}>
          <TabButton
            selected={stage === 'map'}
            onClick={() => setStage('map')}
            label={locale === 'zh' ? '地图' : 'Map'}
            testid="cover-stage-map"
          />
          <TabButton
            selected={stage === 'gallery'}
            onClick={() => setStage('gallery')}
            disabled={!hasMedia}
            label={
              locale === 'zh'
                ? `画廊${media.length > 0 ? ` · ${media.length}` : ''}`
                : `Gallery${media.length > 0 ? ` · ${media.length}` : ''}`
            }
            testid="cover-stage-gallery"
          />
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={mapHostRef}
          className={
            'absolute inset-0 flex min-h-0 flex-col ' +
            (stage === 'map' ? '' : 'pointer-events-none invisible')
          }
        >
          <ErrorBoundary
            scopeLabel="RouteMap"
            resetKey={geometry?.etag ?? 'no-geometry'}
            renderFallback={({ onReset }) => (
              <div
                role="alert"
                className="absolute inset-0 flex min-h-0 flex-col items-center justify-center gap-3 bg-[var(--xr-bg-surface-elev)] p-6 text-center"
                data-testid="route-map-error"
              >
                <p className="text-sm font-medium text-[var(--xr-text-primary)]">
                  {locale === 'zh' ? '地图暂时无法加载' : 'Map temporarily unavailable'}
                </p>
                <p className="max-w-xs text-xs text-[var(--xr-text-secondary)]">
                  {locale === 'zh'
                    ? '页面其它内容仍可正常浏览。点击下方按钮重试或刷新页面。'
                    : 'The rest of the page is still usable. Tap retry below or refresh the page.'}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={onReset}
                    className="inline-flex h-[26px] items-center rounded-[var(--xr-radius-md)] bg-[var(--xr-brand-primary)] px-2.5 text-[11px] font-medium text-[var(--xr-brand-primary-ink)] hover:bg-[var(--xr-brand-primary-hover)]"
                  >
                    {locale === 'zh' ? '重试' : 'Retry'}
                  </button>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="inline-flex h-[26px] items-center rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] px-2.5 text-[11px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface)]"
                  >
                    {locale === 'zh' ? '刷新页面' : 'Reload page'}
                  </button>
                </div>
              </div>
            )}
          >
            <RouteMap
              geometry={geometry}
              onWaypointsStateChange={onWaypointsStateChange}
              focusWaypoint={stage === 'map' ? focusWaypoint : null}
              pinnedWaypoints={pinnedWaypoints}
              activeWaypointId={activeWaypointId}
              onWaypointClick={onWaypointClick}
              attachmentWaypointIds={attachmentWaypointIds}
              showWaypointsOnMap={showWaypointsOnMap}
              styleRequest={styleRequest}
              onStyleChange={onMapStyleChange}
              // Inline editor: drag-enable markers + accept click-to-add
              // mode so RouteEditor can drive snap-to-track behaviour.
              // Per `wiki/decisions/10-route-spatial-and-governance.md` §I.
              editing={canEdit && mapEditMode === 'inline'}
              addingWaypoint={addingWaypoint}
              onWaypointAddRequest={onWaypointAddRequest}
            />
          </ErrorBoundary>
          {canEdit && mapEditMode === 'external' && onEditRoute && (
            <button
              type="button"
              className="absolute right-3 top-3 z-20 rounded-[var(--xr-radius-md)] bg-[var(--xr-brand-primary)] px-3 py-2 text-sm font-medium text-[var(--xr-brand-primary-ink)] shadow-[var(--xr-shadow-sm)] hover:bg-[var(--xr-brand-primary-hover)]"
              onClick={onEditRoute}
              data-testid="route-map-edit-link"
            >
              {locale === 'zh' ? '编辑' : 'Edit'}
            </button>
          )}
        </div>
        {stage === 'gallery' && (
          <GalleryView
            media={media}
            active={active}
            index={galleryIndex}
            total={media.length}
            token={mediaToken}
            onPrev={() =>
              setGalleryIndex((i) =>
                media.length === 0 ? 0 : (i - 1 + media.length) % media.length,
              )
            }
            onNext={() => setGalleryIndex((i) => (media.length === 0 ? 0 : (i + 1) % media.length))}
          />
        )}
      </div>
    </section>
  )
}

function TabButton({
  selected,
  onClick,
  label,
  disabled,
  testid,
}: {
  selected: boolean
  onClick: () => void
  label: string
  disabled?: boolean
  testid?: string
}) {
  // Underlined tab in the spirit of Windows Terminal / Chrome: minimal chrome,
  // only the active tab carries the brand-coloured underline and primary text
  // colour. No box, no segmented borders.
  const base =
    'relative -mb-px h-[30px] px-3 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50'
  const cls = selected
    ? `${base} text-[var(--xr-text-primary)] after:absolute after:inset-x-2 after:bottom-0 after:h-[2px] after:rounded-[var(--xr-radius-full)] after:bg-[var(--xr-brand-primary)]`
    : `${base} text-[var(--xr-text-secondary)] hover:text-[var(--xr-text-primary)]`
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={cls}
    >
      {label}
    </button>
  )
}

interface GalleryViewProps {
  media: MediaAssetPublic[]
  active: MediaAssetPublic | null
  index: number
  total: number
  token?: string | null
  onPrev: () => void
  onNext: () => void
}

function GalleryView({ media, active, index, total, token, onPrev, onNext }: GalleryViewProps) {
  const { t } = useLocale()

  if (!active) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[var(--xr-bg-surface-elev)] text-sm text-[var(--xr-text-muted)]">
        <p>{t('mediaEmpty')}</p>
      </div>
    )
  }
  return (
    <div className="absolute inset-0 flex flex-col bg-[var(--xr-media-bg)]">
      <div className="relative flex min-h-0 flex-1 items-center justify-center">
        {active.kind === 'image' ? (
          <AuthorizedMediaImage
            source={active.medium_url ?? active.blob_url}
            token={token}
            alt={active.alt_text ?? ''}
            className="h-full w-full object-contain"
          />
        ) : (
          <video src={active.blob_url} controls className="h-full w-full" />
        )}

        {/* Top-left: prev / page / next — matches the map's style picker slot. */}
        <div className="pointer-events-none absolute inset-x-2 top-2 z-10 flex items-start justify-between gap-2">
          <div className="pointer-events-auto inline-flex overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]/90 backdrop-blur">
            <button
              type="button"
              aria-label={t('aria.previousMedia')}
              onClick={onPrev}
              disabled={total <= 1}
              className="h-[26px] px-2.5 text-[11px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:opacity-40"
            >
              ‹
            </button>
            <span className="h-[26px] min-w-[3rem] border-l border-r border-[var(--xr-border-default)] px-2.5 text-[11px] font-medium leading-[26px] text-[var(--xr-text-primary)]">
              {index + 1} / {total}
            </span>
            <button
              type="button"
              aria-label={t('aria.nextMedia')}
              onClick={onNext}
              disabled={total <= 1}
              className="h-[26px] px-2.5 text-[11px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {total > 1 && (
        <div className="flex flex-none items-center justify-center gap-1 border-t border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] py-2">
          {media.map((m, i) => (
            <span
              key={m.id}
              className={
                'h-1.5 w-5 rounded-[var(--xr-radius-full)] transition ' +
                (i === index ? 'bg-[var(--xr-brand-primary)]' : 'bg-[var(--xr-border-default)]')
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}
